import { Box, LinearProgress, Typography } from "@mui/material"
import type { SortingState } from "@tanstack/react-table"
import type { ReactElement } from "react"
import React from "react"
import { pluralize } from "utils/stringUtils"

import type { Instruction } from "@/graphql/codegen/graphql"
import { InstructionsToAssociateTable } from "@/screens/Products/components/Product/Instructions/components/tables/InstructionsToAssociateTable"
import type { AutocompleteOption } from "@/types/form-fields"

const InstructionsArea = ({
  instructions,
  fetchingInstructions,
  onInstructionSelection,
  onInstructionSorted,
  associatedInstructionIds,
  selected,
  searchQuery,
  sorting,
}: {
  searchQuery: string | AutocompleteOption
  instructions: Instruction[]
  fetchingInstructions: boolean
  onInstructionSelection: (selected: Record<string, boolean>) => void
  onInstructionSorted: (sorting: SortingState) => void
  associatedInstructionIds: string[]
  selected: Record<string, boolean>
  sorting: SortingState
}): ReactElement => {
  const isSearchQueryString = typeof searchQuery === "string"
  const instructionsCount = instructions.length ?? 0
  const noInstructionsFound = instructionsCount === 0 && !fetchingInstructions
  const noInstructionsFoundMessage = `No instructions found for "${isSearchQueryString ? searchQuery : searchQuery.label}"`

  if (fetchingInstructions) {
    return <LinearProgress className='sticky top-0 z-10' />
  }
  const itemsText = pluralize(instructionsCount, "instruction", "instructions")

  return (
    <Box>
      <Box className='mb-6 flex align-middle'>
        <Typography className='inline-flex align-middle text-sm leading-6'>
          <Typography className='inline-flex font-semibold text-gray-800'>
            {instructionsCount} {itemsText} found
          </Typography>
          , Select instruction/s add to the product.
        </Typography>
      </Box>
      {noInstructionsFound ? (
        <Box className='flex flex-col items-center justify-center'>
          <Typography className='text-gray-500'>{noInstructionsFoundMessage}</Typography>
        </Box>
      ) : (
        <Box className='max-h-[400px] overflow-auto'>
          <InstructionsToAssociateTable
            instructions={instructions}
            onInstructionSelection={onInstructionSelection}
            onInstructionSorted={onInstructionSorted}
            associatedInstructionIds={associatedInstructionIds}
            selected={selected}
            sorting={sorting}
          />
        </Box>
      )}
    </Box>
  )
}

export default InstructionsArea
